import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { JobFillOutAction } from './job-fill-out.action';
import { ResetStateDefaults } from "../base-action-class.model";
import { finalize, from, map, Observable, of, switchMap, zip } from "rxjs";
import { throwError } from "rxjs/internal/observable/throwError";
import { Job } from "../../models/job/Job";
import { JobSerialization } from "../../enums/job/JobSerialization";
import { CompoApp } from "../../services/CompoApp";
import { catchError, combineLatestWith, delay } from "rxjs/operators";
import { calcValue, RequestClientService } from "../../services/client/request-client.service";
import { JobUpdateView } from "../../models/job/JobUpdateView";
import { ChecklistRequestService } from "../../services/request/checklist-request.service";
import { JobRequestService } from "../../services/request/job-request.service";
import { Message } from "../../models/message/Message";
import { DocumentModel } from "../../models/document/document.model";
import { FileUploadError, FileUploaderUtils } from "../../content/components/file-uploader/FileUploaderUtils";
import { ProcessUtils } from "../../utils/ProcessUtils";
import { DocumentGroupReferenceType } from "../../enums/document/DocumentGroupReferenceType";
import { DocumentGroupModel } from "../../models/document/document-group.model";
import { ObjectRequestService } from "../../services/request/object-request.service";
import {
  WorkflowOverviewEntry,
  WorkflowOverviewGroup,
  WorkflowOverviewItem,
  WorkflowOverviewItemState
} from "../../models/workflow-overview/workflow-overview.model";
import { ChecklistInfo } from "../../models/checklist/checklist-info.model";
import { ChecklistStatus } from "../../enums/checklist/checklist-status.enum";
import { CompogeniusNaviTypeEnum } from '../../services/client/navigation-service/compogenius-navi-type.enum';
import { Router } from '@angular/router';
import {
  RoutesNavigationClientService
} from '../../services/client/navigation-service/routes-navigation-client.service';
import { MessageDesignBUtils } from '../../content/components/message-design-b/message-design-b.utils';
import { JobTemplateModel } from '../../models/job_template/job-template.model';
import { ChatCounts } from '../../models/chat/chat-counts.model';
import { User } from '../../models/user/User';
import { RJobXsState } from '../user-rights/topic-based/job/r-job-xs.state';
import { JobStatus } from '../../enums/job/JobStatus';
import SaveJob = JobFillOutAction.SaveJob;
import AutoSave = JobFillOutAction.AutoSave;
import { JobService } from '../../services/job.service';
import { ChatService } from '../../services/ChatService';
import { JobRoutineService } from '../../services/job-routine.service';
import { JobFillOutAccordion } from './job-fill-out-accordion.enum';

interface JobFillOutXsStateModel {
  jobId: string;
  job: Job;
  updatedJob: JobUpdateView;
  jobRoutine: JobTemplateModel;
  jobsOfJob: Job[];
  checklistInfos: ChecklistInfo[];
  checklistEditMode: boolean;
  messagesOfJob: Message[];
  files: DocumentModel[];
  isObjectOutOfOrder: boolean;
  chatCounts: ChatCounts;
  workflowOverviewItems: WorkflowOverviewItem[];
  visibleAccordions: Set<JobFillOutAccordion>;
  accordionToOpen: JobFillOutAccordion;
  inProgress: boolean;
  ownUserRegisteredForFillOut: boolean;
}

const getDefaults = (): JobFillOutXsStateModel => ({
  jobId: null,
  job: null,
  updatedJob: null,
  jobRoutine: null,
  jobsOfJob: [],
  checklistInfos: [],
  checklistEditMode: false,
  messagesOfJob: [],
  files: [],
  isObjectOutOfOrder: null,
  chatCounts: null,
  workflowOverviewItems: null,
  visibleAccordions: new Set<JobFillOutAccordion>(),
  accordionToOpen: null,
  inProgress: false,
  ownUserRegisteredForFillOut: false,
});

@State<JobFillOutXsStateModel>({
  name: 'jobFillOut',
  defaults: getDefaults()
})
@Injectable()
export class JobFillOutXsState {

  //region READONLY FIELDS
  private static readonly REQUEST_DELAY: number = 500;
  readonly #jobRequestService = inject(JobService);
  readonly #chatService = inject(ChatService);
  readonly #jobRoutineTemplate = inject(JobRoutineService);
  //endregion

  //region CONSTRUCTOR
  constructor(
    private app: CompoApp,
    private checklistRequestService: ChecklistRequestService,
    private jobRequestService: JobRequestService,
    private store: Store,
    private router: Router,
    private compogeniusRoutes: RoutesNavigationClientService,
    private objectRequestService: ObjectRequestService
  ) {
  }

  //endregion

  //region SELECTORS
  @Selector()
  static jobId(state: JobFillOutXsStateModel): string {
    return state.jobId;
  }

  @Selector()
  static checklistEditMode(state: JobFillOutXsStateModel): boolean {
    return state.checklistEditMode;
  }

  @Selector()
  static job(state: JobFillOutXsStateModel): Job {
    return state.job;
  }

  @Selector()
  static jobRoutine(state: JobFillOutXsStateModel): JobTemplateModel {
    return state.jobRoutine;
  }

  @Selector()
  static jobsOfJob(state: JobFillOutXsStateModel): Job[] {
    return state.jobsOfJob;
  }

  @Selector()
  static checklistInfos(state: JobFillOutXsStateModel): ChecklistInfo[] {
    return state.checklistInfos;
  }

  @Selector()
  static messagesOfJob(state: JobFillOutXsStateModel): Message[] {
    return state.messagesOfJob ? JSON.parse(JSON.stringify(state.messagesOfJob)) : [];
  }

  @Selector()
  static files(state: JobFillOutXsStateModel): DocumentModel[] {
    return state.files;
  }

  @Selector()
  static isObjectOutOfOrder(state: JobFillOutXsStateModel): boolean {
    return state.isObjectOutOfOrder;
  }

  @Selector()
  static chatCounts(state: JobFillOutXsStateModel): ChatCounts {
    return state.chatCounts;
  }

  @Selector()
  static workflowOverviewItems(state: JobFillOutXsStateModel): WorkflowOverviewItem[] {
    return state.workflowOverviewItems;
  }

  @Selector()
  static visibleJobAccordions(state: JobFillOutXsStateModel): Set<JobFillOutAccordion> | null {
    return state.visibleAccordions;
  }

  @Selector()
  static accordionToOpen(state: JobFillOutXsStateModel): JobFillOutAccordion {
    return state.accordionToOpen;
  }

  @Selector()
  static hasUnsavedChanges(state: JobFillOutXsStateModel): boolean {
    return !!state.updatedJob;
  }

  @Selector()
  static inProgress(state: JobFillOutXsStateModel): boolean {
    return state.inProgress;
  }

  @Selector()
  static ownUserRegisteredForFillOut(state: JobFillOutXsStateModel): boolean {
    return state.ownUserRegisteredForFillOut;
  }

  @Selector()
  static activeFillingOutUser(state: JobFillOutXsStateModel): User {
    return state.job?.activeFillingOutUser;
  }

  @Selector([
    JobFillOutXsState.ownUserRegisteredForFillOut,
    RJobXsState.canEditJob
  ])
  static fillOutEnabled(
    ownUserRegisteredForFillOut: boolean,
    canEditJob: boolean
  ): boolean {
    return ownUserRegisteredForFillOut && canEditJob;
  }

  //endregion

  //region ACTIONS
  @Action(JobFillOutAction.Init)
  public init(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.Init
  ): Observable<void> {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (action.jobId === null) {
      return throwError(() => new Error('Job id cannot be null!'));
    }

    if (state.jobId) {
      return throwError(() => new Error('State is already initialized!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    return this.fetchJobAndRelatedEntities(ctx, action.jobId);
  }

  @Action(JobFillOutAction.ReloadJob)
  public reloadJob(
    ctx: StateContext<JobFillOutXsStateModel>
  ): Observable<void> {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (state.jobId === null) {
      throw new Error('State is not initialized!');
    }

    if (state.inProgress === null) {
      throw new Error('Request is in progress!');
    }

    return this.fetchJobAndRelatedEntities(ctx, state.jobId);
  }

  @Action(JobFillOutAction.ReloadChecklists)
  public reloadChecklists(
    ctx: StateContext<JobFillOutXsStateModel>
  ): Observable<void> {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (state.jobId === null) {
      throw new Error('State is not initialized!');
    }

    if (state.inProgress === null) {
      throw new Error('Request is in progress!');
    }

    ctx.patchState({
      inProgress: true,
      checklistInfos: []
    });

    const requestStartDate: Date = new Date();

    return this.checklistRequestService.getAllChecklistsForJob(state.jobId)
      .pipe(
        delay(calcValue(+requestStartDate, JobFillOutXsState.REQUEST_DELAY)),
        map((checklistInfos: ChecklistInfo[]) => {
          const visibleAccordions = new Set(state.visibleAccordions);

          if (checklistInfos.length > 0) {
            visibleAccordions.add(JobFillOutAccordion.CHECKLISTS);
          } else {
            visibleAccordions.delete(JobFillOutAccordion.CHECKLISTS);
          }

          ctx.patchState({
            checklistInfos,
            visibleAccordions
          });

          this.setWorkflowOverview(ctx);
        }),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  // if the user remove an added but not saved reference the item must be removed from the info list
  @Action(JobFillOutAction.RemoveChecklistInfoWithoutSave)
  public removeChecklistInfoWithoutSave(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.RemoveChecklistInfoWithoutSave
  ): void {
    if (!action.checklistTemplateId) {
      throw new Error('checklistTemplateId cannot be null!');
    }

    const state = ctx.getState();
    const updatedChecklistInfos = state.checklistInfos.filter(ci => ci.checklistTemplateId !== action.checklistTemplateId);

    // Update the state with the new array
    ctx.setState({
      ...state,
      checklistInfos: updatedChecklistInfos,
    });

  }

  // if the user add a new reference the newly added but not saved reference should be displayed in the checkListInfoList
  @Action(JobFillOutAction.AddChecklistInfoWithoutSave)
  public addChecklistInfoWithoutSave(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.AddChecklistInfoWithoutSave
  ): void {
    if (!action.checklistInfo) {
      throw new Error('checklistInfo cannot be null!');
    }

    const state = ctx.getState();
    const updatedChecklistInfos = [...state.checklistInfos, action.checklistInfo];

    // Update the state with the new array
    ctx.setState({
      ...state,
      checklistInfos: updatedChecklistInfos,
    });
  }

  @Action(JobFillOutAction.ShowAccordion)
  public showAccordion(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.ShowAccordion
  ): void {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (state.jobId === null) {
      throw new Error('State is not initialized!');
    }

    if (state.inProgress === null) {
      throw new Error('Request is in progress!');
    }

    if (action.accordion === JobFillOutAccordion.CHECKLISTS
      && action.editMode
      && !this.store.selectSnapshot(RJobXsState.canEditJob)
    ) {
      throw new Error('Cannot open CHECKLISTS accordion in edit mode: no privilege to edit job!');
    }

    this.showAccordions(ctx, [action.accordion], action.editMode);
    ctx.patchState({ accordionToOpen: action.accordion });
    this.setWorkflowOverview(ctx);

    setTimeout(() => {
      this.store.dispatch(new JobFillOutAction.FocusAccordion(action.accordion));
    });
  }

  @Action(JobFillOutAction.HideAccordion)
  public hideAccordion(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.HideAccordion
  ): void {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (state.jobId === null) {
      throw new Error('State is not initialized!');
    }

    if (state.inProgress === null) {
      throw new Error('Request is in progress!');
    }

    this.hideAccordions(ctx, [action.accordion]);
    this.setWorkflowOverview(ctx);
  }

  @Action(JobFillOutAction.UpdateJobAssessment)
  public updateJobAssessment(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.UpdateJobAssessment
  ): void {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (!action.updatedJob) {
      throw new Error('Updated job cannot be null!');
    }
    if (state.jobId === null) {
      throw new Error('State is not initialized!');
    }
    if (state.inProgress === null) {
      throw new Error('Request is in progress!');
    }

    const updatedJob: JobUpdateView = {...(state.updatedJob || new JobUpdateView())};
    updatedJob.incidentCode = action.updatedJob.incidentCode;
    updatedJob.overallAssessmentId = action.updatedJob.overallAssessmentId;
    updatedJob.feedBack = action.updatedJob.feedBack;

    ctx.patchState({
      updatedJob
    });
  }

  @Action(JobFillOutAction.UpdateJobTime)
  public updateJobTime(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.UpdateJobTime
  ): void {
    const state: JobFillOutXsStateModel = ctx.getState();
    if (!action.updatedJob) {
      throw new Error('Updated job cannot be null!');
    }
    if (state.jobId === null) {
      throw new Error('State is not initialized!');
    }
    if (state.inProgress === null) {
      throw new Error('Request is in progress!');
    }

    const updatedJob: JobUpdateView = {...(state.updatedJob || new JobUpdateView())};
    updatedJob.actualWorkingTime = action.updatedJob.actualWorkingTime;

    ctx.patchState({
      updatedJob
    });
  }

  @Action(JobFillOutAction.UpdateJobSchedule)
  public updateJobSchedule(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.UpdateJobSchedule
  ): void {
    const state: JobFillOutXsStateModel = ctx.getState();
    if (!action.updatedJob) {
      throw new Error('Updated job cannot be null!');
    }
    if (state.jobId === null) {
      throw new Error('State is not initialized!');
    }
    if (state.inProgress === null) {
      throw new Error('Request is in progress!');
    }

    const updatedJob: JobUpdateView = {...(state.updatedJob || new JobUpdateView())};
    updatedJob.plannedImplDateStart = action.updatedJob.plannedImplDateStart;
    updatedJob.plannedImplDateEnd = action.updatedJob.plannedImplDateEnd;
    updatedJob.allDayPlannedDate = action.updatedJob.allDayPlannedDate;
    updatedJob.actualWorkingTime = action.updatedJob.actualWorkingTime;
    updatedJob.standstill = action.updatedJob.standstill;
    updatedJob.isIdentical = action.updatedJob.isIdentical;
    updatedJob.standStillDateStart = action.updatedJob.standStillDateStart;
    updatedJob.standStillDateEnd = action.updatedJob.standStillDateEnd;
    updatedJob.allDayStandStill = action.updatedJob.allDayStandStill;

    ctx.patchState({
      updatedJob
    });
  }

  @Action(JobFillOutAction.UpdateJobInfosFromFillOutPage)
  public updateJobInfos(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.UpdateJobInfosFromFillOutPage
  ): void {
    const state: JobFillOutXsStateModel = ctx.getState();
    if (!action.updatedJob) {
      throw new Error('Updated job cannot be null!');
    }
    if (state.jobId === null) {
      throw new Error('State is not initialized!');
    }
    if (state.inProgress === null) {
      throw new Error('Request is in progress!');
    }

    const updatedJob: JobUpdateView = {...(state.updatedJob || new JobUpdateView())};
    updatedJob.assigneeGroupId = action.updatedJob.assigneeGroupId;
    updatedJob.assigneeId = action.updatedJob.assigneeId;
    // description is edited and saved separately
    updatedJob.inHouseExecuted = action.updatedJob.inHouseExecuted;
    updatedJob.jobType = action.updatedJob.jobTypeId;
    updatedJob.name = action.updatedJob.name;
    updatedJob.orderNumber = action.updatedJob.orderNumber;
    updatedJob.serviceProviderId = action.updatedJob.serviceProviderId;
    updatedJob.executionPlaceServiceProviderId = action.updatedJob.executionPlaceServiceProviderId;
    updatedJob.producerNumber = action.updatedJob.producerNumber;
    updatedJob.flatRate = action.updatedJob.flatRate;
    updatedJob.plannedWorkingTime = action.updatedJob.plannedWorkingTime;

    ctx.patchState({
      updatedJob
    });
  }

  @Action(JobFillOutAction.UpdateJobDetails)
  public updateJobDetails(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.UpdateJobDetails
  ): void {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (!action.attributes) {
      throw new Error('Attributes cannot be null!');
    }

    if (state.jobId === null) {
      throw new Error('State is not initialized!');
    }

    if (state.inProgress === null) {
      throw new Error('Request is in progress!');
    }

    const updatedJob: JobUpdateView = {...(state.updatedJob || new JobUpdateView())};
    updatedJob.attributes = action.attributes;

    ctx.patchState({
      updatedJob
    });
  }

  @Action(JobFillOutAction.UpdateJobMessages)
  public updateJobMessages(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.UpdateJobMessages
  ): void {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (state.jobId === null) {
      throw new Error('State is not initialized!');
    }

    if (state.inProgress === null) {
      throw new Error('Request is in progress!');
    }

    const updatedJob: JobUpdateView = {...(state.updatedJob || new JobUpdateView())};
    updatedJob.newMessageIds = action.newMessageIds || [];
    updatedJob.removedMessageIds = action.removedMessageIds || [];

    ctx.patchState({
      updatedJob
    });
  }

  @Action(JobFillOutAction.TogglePinned)
  public togglePinned(
    ctx: StateContext<JobFillOutXsStateModel>
  ): Observable<void> {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (state.jobId === null) {
      return throwError(() => new Error('State is not initialized!'));
    }

    if (state.inProgress === null) {
      return throwError(() => new Error('Request is in progress!'));
    }

    const job: JobUpdateView = new JobUpdateView();
    job.id = state.jobId;
    job.isPinned = !state.job.isPinned;

    ctx.patchState({
      inProgress: true
    });

    const requestStartDate: Date = new Date();

    return from(this.#jobRequestService.updateJob(job))
      .pipe(
        delay(calcValue(+requestStartDate, JobFillOutXsState.REQUEST_DELAY)),
        map(
          (job: Job) => {
            ctx.patchState({
              job: job
            });
          }
        ),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  @Action(JobFillOutAction.UploadFile)
  public uploadFile(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.UploadFile
  ): Observable<void> {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (!action.file) {
      return throwError(() => new Error('File cannot be null!'));
    }

    if (state.jobId === null) {
      return throwError(() => new Error('State is not initialized!'));
    }

    if (state.inProgress === null) {
      return throwError(() => new Error('Request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const requestStartDate: Date = new Date();

    return from(FileUploaderUtils.uploadFile(action.file, this.app))
      .pipe(
        catchError((err: FileUploadError) => {
          if (err === FileUploadError.FILE_TOO_BIG) {
            this.app.on.inProgressAsyncWorkFinished$.next(MessageDesignBUtils.fileUploadFileSizeTooBig());
          }
          return of(null);
        }),
        switchMap((file: DocumentModel) => {
          if (!file) {
            return of(null);
          }
          const updatedJob: JobUpdateView = new JobUpdateView();
          updatedJob.id = state.jobId;
          const newDocuments: DocumentGroupModel[] = [];
          const newDocument: DocumentGroupModel[] = ProcessUtils.initNewFilesByEditProcess(
            [file],
            DocumentGroupReferenceType.JOB,
            state.jobId
          );
          newDocuments.push(...newDocument);
          updatedJob.newDocuments = newDocuments;

          return this.#jobRequestService.updateJob(updatedJob);
        }),
        delay(calcValue(+requestStartDate, JobFillOutXsState.REQUEST_DELAY)),
        map((job: Job) => {
            if (!job) {
              return;
            }
            ctx.patchState({
              job,
              files: job.documents.map(documentGroup => DocumentGroupModel.allDocumentsFromGroup(documentGroup)[0])
            });
            this.showAccordions(ctx, [JobFillOutAccordion.FILES]);
            this.setWorkflowOverview(ctx);
            this.store.dispatch(new JobFillOutAction.FocusAccordion(JobFillOutAccordion.FILES));
          }
        ),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  @Action(JobFillOutAction.UploadFiles)
  public uploadMultipleFiles(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.UploadFiles
  ): Observable<void> {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (!action.files) {
      return throwError(() => new Error('File cannot be null!'));
    }

    if (state.jobId === null) {
      return throwError(() => new Error('State is not initialized!'));
    }

    if (state.inProgress === null) {
      return throwError(() => new Error('Request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const requestStartDate: Date = new Date();

    return from(FileUploaderUtils.uploadMultipleFiles(action.files, this.app))
      .pipe(
        catchError((err: FileUploadError) => {
          if (err === FileUploadError.FILE_TOO_BIG) {
            this.app.on.inProgressAsyncWorkFinished$.next(MessageDesignBUtils.fileUploadFileSizeTooBig());
          }
          return of(null);
        }),
        switchMap((files: DocumentModel[]) => {
          if (!files) {
            return of(null);
          }
          const updatedJob: JobUpdateView = new JobUpdateView();
          updatedJob.id = state.jobId;
          const newDocuments: DocumentGroupModel[] = [];
          const newDocument: DocumentGroupModel[] = ProcessUtils.initNewFilesByEditProcess(
            files,
            DocumentGroupReferenceType.JOB,
            state.jobId
          );
          newDocuments.push(...newDocument);
          updatedJob.newDocuments = newDocuments;

          return this.#jobRequestService.updateJob(updatedJob);
        }),
        delay(calcValue(+requestStartDate, JobFillOutXsState.REQUEST_DELAY)),
        map((job: Job) => {
            if (!job) {
              return;
            }
            ctx.patchState({
              job,
              files: job.documents.map(documentGroup => DocumentGroupModel.allDocumentsFromGroup(documentGroup)[0])
            });
            this.showAccordions(ctx, [JobFillOutAccordion.FILES]);
            this.setWorkflowOverview(ctx);
            this.store.dispatch(new JobFillOutAction.FocusAccordion(JobFillOutAccordion.FILES));
          }
        ),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  @Action(JobFillOutAction.DeleteFile)
  public deleteFile(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.DeleteFile
  ): Observable<void> {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (!action.file?.documentGroupId) {
      return throwError(() => new Error('File cannot be null!'));
    }

    if (state.jobId === null) {
      return throwError(() => new Error('State is not initialized!'));
    }

    if (state.inProgress === null) {
      return throwError(() => new Error('Request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const requestStartDate: Date = new Date();

    const updatedJob: JobUpdateView = new JobUpdateView();
    updatedJob.id = state.jobId;
    updatedJob.deletedDocumentGroupIds = [action.file.documentGroupId];


    return from(this.#jobRequestService.updateJob(updatedJob))
      .pipe(
        delay(calcValue(+requestStartDate, JobFillOutXsState.REQUEST_DELAY)),
        map(
          (job: Job) => {
            ctx.patchState({
              job: job,
              files: job.documents.map(documentGroup => DocumentGroupModel.allDocumentsFromGroup(documentGroup)[0])
            });
            if (job.documents?.length === 0) {
              this.hideAccordions(ctx, [JobFillOutAccordion.FILES]);
            }
            this.setWorkflowOverview(ctx);
          }
        ),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  @Action(JobFillOutAction.SetJobStatus)
  public setJobStatus(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.SetJobStatus
  ): Observable<void> {

    const state: JobFillOutXsStateModel = ctx.getState();

    if (!action.jobStatus) {
      return throwError(() => new Error('File cannot be null!'));
    }

    if (state.jobId === null) {
      return throwError(() => new Error('State is not initialized!'));
    }

    if (state.inProgress === null) {
      return throwError(() => new Error('Request is in progress!'));
    }

    const jobUpdateView: JobUpdateView = new JobUpdateView();
    jobUpdateView.id = state.jobId;
    jobUpdateView.status = action.jobStatus;
    if (
      state.job?.isPinned
      && [JobStatus.DONE, JobStatus.CLOSED, JobStatus.CANCELLED].includes(action.jobStatus)
    ) {
      jobUpdateView.isPinned = false;
    }
    if (action.shortenEndDateOfOutOfOrder === true) {
      jobUpdateView.shortenEndDateOfOutOfOrder = true;
    }
    if (action.implDateStart) {
      jobUpdateView.implDateStart = action.implDateStart;
    }
    if (action.implDateEnd) {
      jobUpdateView.implDateEnd = action.implDateEnd;
    }
    if (action.allDayPlannedDate != null) {
      jobUpdateView.allDayPlannedDate = action.allDayPlannedDate;
    }

    ctx.patchState({
      inProgress: true
    });

    const requestStartDate: Date = new Date();

    return from(this.#jobRequestService.updateJob(jobUpdateView))
      .pipe(
        combineLatestWith(
          (state.job.componentId || state.job.component?.id)
            ? this.objectRequestService.isObjectOutOfOrder(state.job.componentId || state.job.component.id)
            : of(false)
        ),
        delay(calcValue(+requestStartDate, JobFillOutXsState.REQUEST_DELAY)),
        map(([job, isObjectOutOfOrder]: [Job, boolean]) => {
          ctx.patchState({
            job,
            isObjectOutOfOrder,
            updatedJob: null
          });
          this.app.on.processJobRefreshNeeded$.next();
        }),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  @Action([
    JobFillOutAction.SaveJob,
    JobFillOutAction.AutoSave,
  ])
  public saveJob(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.SaveJob | JobFillOutAction.AutoSave
  ): Observable<void> {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (state.jobId === null) {
      return throwError(() => new Error('State is not initialized!'));
    }

    if (state.inProgress === null) {
      return throwError(() => new Error('Request is in progress!'));
    }

    if (!state.updatedJob) {
      return of();
    }

    ctx.patchState({
      inProgress: true
    });

    const requestStartDate: Date = new Date();

    const updatedJob: JobUpdateView = {...state.updatedJob};
    updatedJob.id = state.jobId;

    return from(this.#jobRequestService.updateJob(updatedJob))
      .pipe(
        delay(calcValue(+requestStartDate, JobFillOutXsState.REQUEST_DELAY)),
        map(
            (job: Job) => {
              const prevState: JobFillOutXsStateModel = ctx.getState();
              let newUpdatedJob: any = null;

              const jobToSave = {...job};

              if (action instanceof AutoSave) {
                jobToSave.description = prevState.updatedJob?.description ?? prevState.job?.description;
              }

              if (prevState.updatedJob) {
                const remaining: any = {...(prevState.updatedJob as any)};

                Object.keys(updatedJob as any).forEach((key) => {
                  if (key === 'id') return;
                  if (action instanceof AutoSave && key === 'description') return;
                  delete remaining[key];
                });

                const hasOther = Object.values(remaining)
                  .some((v: any) => v !== undefined && v !== null && (!(Array.isArray(v)) || v.length > 0));
                newUpdatedJob = hasOther ? remaining : null;
              }

              // keep messagesOfJob and visible accordions in sync with returned job
              const visibleAccordions: Set<JobFillOutAccordion> = new Set(prevState.visibleAccordions);
              if (jobToSave?.messages?.length > 0) {
                visibleAccordions.add(JobFillOutAccordion.MESSAGES_OF_JOB);
              } else {
                visibleAccordions.delete(JobFillOutAccordion.MESSAGES_OF_JOB);
              }

              ctx.patchState({
                job: jobToSave,
                updatedJob: newUpdatedJob,
                messagesOfJob: jobToSave?.messages ?? [],
                visibleAccordions
              });

              this.setWorkflowOverview(ctx);

              if (!(action instanceof AutoSave)) {
                this.app.on.processJobRefreshNeeded$.next();
              }
            }
        ),
        finalize(() => {
          ctx.patchState({inProgress: false});
          if (action instanceof SaveJob) {
            this.hideAccordions(ctx, [
              JobFillOutAccordion.UPDATE_JOB_INFOS,
              JobFillOutAccordion.RESCHEDULE_APPOINTMENT
            ]);
          }
        })
      );
  }

  @Action(JobFillOutAction.JobOutOfJobCreated)
  public jobOutOfJobCreated(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.JobOutOfJobCreated,
  ): void {
    this.resetState(ctx, new JobFillOutAction.ResetState());
    this.app.on.processJobRefreshNeeded$.next();

    this.router.navigateByUrl(
      this.compogeniusRoutes
        .getCommand(CompogeniusNaviTypeEnum.PROCESS_JOB_FILL_OUT)
        .replace(':jobId', '' + action.jobId)
    );

  }

  @Action([
    JobFillOutAction.UpdateJobInfos,
    JobFillOutAction.RescheduleAppointment
  ])
  public updateJob(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.RescheduleAppointment | JobFillOutAction.UpdateJobInfos
  ): Observable<void> {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (action.job === null) {
      return throwError(() => new Error('Job cannot be null!'));
    }

    if (state.jobId === null) {
      return throwError(() => new Error('State is not initialized!'));
    }

    if (state.inProgress === null) {
      return throwError(() => new Error('Request is in progress!'));
    }

    const job: JobUpdateView = action.job;
    job.id = state.jobId;

    ctx.patchState({
      inProgress: true
    });

    const requestStartDate: Date = new Date();

    return from(this.#jobRequestService.updateJob(job))
      .pipe(
        delay(calcValue(+requestStartDate, JobFillOutXsState.REQUEST_DELAY)),
        map(
          (job: Job) => {
            ctx.patchState({
              job: job
            });
            let accordionToHide: JobFillOutAccordion;
            if (action instanceof JobFillOutAction.UpdateJobInfos) {
              accordionToHide = JobFillOutAccordion.UPDATE_JOB_INFOS;
            } else if (action instanceof JobFillOutAction.RescheduleAppointment) {
              accordionToHide = JobFillOutAccordion.RESCHEDULE_APPOINTMENT;
            }
            this.hideAccordions(ctx, [accordionToHide]);
            this.setWorkflowOverview(ctx);
            this.app.on.processJobRefreshNeeded$.next();


          }
        ),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  @Action(JobFillOutAction.ReloadChatCounts)
  public reloadChatCounts(ctx: StateContext<JobFillOutXsStateModel>): Observable<void> {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (state.jobId === null) {
      return throwError(() => new Error('State is not initialized!'));
    }

    return (state.job.chatId
        ? this.#chatService.getChatCounts(state.job.chatId)
        : of(null)
    ).pipe(map((chatCounts: ChatCounts) => {
      ctx.patchState({chatCounts});
      this.setWorkflowOverview(ctx);
    }));
  }

  @Action(JobFillOutAction.UpdateJobDescription)
  public updateJobDescription(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.UpdateJobDescription
  ): Observable<void> {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (state.jobId === null) {
      return throwError(() => new Error('State is not initialized!'));
    }

    if (state.inProgress === null) {
      return throwError(() => new Error('Request is in progress!'));
    }

    const updatedJob: JobUpdateView = new JobUpdateView();
    updatedJob.id = state.jobId;
    updatedJob.description = action.description ?? '';

    ctx.patchState({inProgress: true});

    const requestStartDate: Date = new Date();

    return from(this.#jobRequestService.updateJob(updatedJob))
      .pipe(
        delay(calcValue(+requestStartDate, JobFillOutXsState.REQUEST_DELAY)),
        map((job: Job) => {
          ctx.patchState({job});
          this.store.dispatch(new JobFillOutAction.UpdateJobDescriptionSuccess(state.jobId, action.description ?? ''));
          // clear only the description draft from updatedJob (preserve other unsaved changes)
          const currentState: JobFillOutXsStateModel = ctx.getState();
          if (currentState.updatedJob) {
            const clone: any = {...(currentState.updatedJob as any)};
            delete clone.description;
            const hasOther = Object.values(clone).some((v: any) => v !== undefined && v !== null && (!(Array.isArray(v)) || v.length > 0));
            ctx.patchState({updatedJob: hasOther ? (clone as any) : null});
          }
        }),
        catchError((error) => {
          this.store.dispatch(new JobFillOutAction.UpdateJobDescriptionFailure(error));
          return of(void 0);
        }),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  @Action(JobFillOutAction.UpdateServiceProviderNoteDraft)
  public updateServiceProviderNoteDraft(
    ctx: StateContext<JobFillOutXsStateModel>,
    action:JobFillOutAction.UpdateServiceProviderNoteDraft
  ): void {
    const state: JobFillOutXsStateModel = ctx.getState();
    if (state.jobId === null) {
      throw new Error('State is not initialized!');
    }
    if (state.inProgress === null) {
      throw new Error('Request is in progress!');
    }

    const currentNote: string = state.job?.serviceProviderNote ?? '';
    const isSame: boolean = (action.serviceProviderNote ?? '') === currentNote;

    if (isSame) {
      // remove draft serviceProviderNote and clear updatedJob if no other changes pending
      if (state.updatedJob) {
        const clone: any = {...(state.updatedJob as any)};
        delete clone.serviceProviderNote;
        const hasOther = Object.values(clone).some(
          (v: any) => v !== undefined && v !== null && (!(Array.isArray(v)) || v.length > 0));
        ctx.patchState({updatedJob: hasOther ? (clone as any) : null});
      }
      return;
    }

    const updatedJob: JobUpdateView = {...(state.updatedJob || new JobUpdateView())} as JobUpdateView;
    (updatedJob as any).serviceProviderNote = action.serviceProviderNote ?? '';
    ctx.patchState({updatedJob});
  }

  @Action(JobFillOutAction.UpdateJobDescriptionDraft)
  public updateJobDescriptionDraft(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.UpdateJobDescriptionDraft
  ): void {
    const state: JobFillOutXsStateModel = ctx.getState();
    if (state.jobId === null) {
      throw new Error('State is not initialized!');
    }
    if (state.inProgress === null) {
      throw new Error('Request is in progress!');
    }

    const currentDescription: string = state.job?.description ?? '';
    const isSame: boolean = (action.description ?? '') === currentDescription;

    if (isSame) {
      // remove draft description and clear updatedJob if no other changes pending
      if (state.updatedJob) {
        const clone: any = {...(state.updatedJob as any)};
        delete clone.description;
        const hasOther = Object.values(clone).some((v: any) => v !== undefined && v !== null && (!(Array.isArray(v)) || v.length > 0));
        ctx.patchState({updatedJob: hasOther ? (clone as any) : null});
      }
      return;
    }

    const updatedJob: JobUpdateView = {...(state.updatedJob || new JobUpdateView())} as JobUpdateView;
    (updatedJob as any).description = action.description ?? '';
    ctx.patchState({updatedJob});
  }

  @Action(JobFillOutAction.RegisterActiveFillingOutUser)
  public registerActiveFillingOutUser(
    ctx: StateContext<JobFillOutXsStateModel>
  ): Observable<void> {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (state.jobId === null) {
      return throwError(() => new Error('State is not initialized!'));
    }

    return this.jobRequestService.registerActiveFillingOutUser(state.jobId)
      .pipe(
        map((registered: boolean) => {
          ctx.patchState({
            ownUserRegisteredForFillOut: registered
          });
        })
      );
  }

  @Action(JobFillOutAction.UnregisterActiveFillingOutUser)
  public unregisterActiveFillingOutUser(
    ctx: StateContext<JobFillOutXsStateModel>
  ): Observable<void> {
    const state: JobFillOutXsStateModel = ctx.getState();

    if (state.jobId === null) {
      return throwError(() => new Error('State is not initialized!'));
    }

    return this.jobRequestService.unregisterActiveFillingOutUser(state.jobId)
      .pipe(
        map(() => {
          ctx.patchState({
            ownUserRegisteredForFillOut: false
          });
        })
      );
  }

  @Action([
    JobFillOutAction.ResetState,
    ResetStateDefaults
  ])
  public resetState(
    ctx: StateContext<JobFillOutXsStateModel>,
    action: JobFillOutAction.ResetState | ResetStateDefaults
  ): void {
    const keepFillOutLock: boolean = action instanceof JobFillOutAction.ResetState && action.keepFillOutLock;

    if (!keepFillOutLock
      && ctx.getState().ownUserRegisteredForFillOut
      && ctx.getState().jobId != null
    ) {
      this.jobRequestService
        .unregisterActiveFillingOutUser(ctx.getState().jobId)
        .subscribe();
    }
    ctx.setState(getDefaults());
  }

  //endregion

  //region UTILS
  private fetchJobAndRelatedEntities(ctx: StateContext<JobFillOutXsStateModel>, jobId: string): Observable<void> {
    if (!jobId) {
      return of();
    }

    ctx.patchState({
      inProgress: true
    });

    const requestStartDate: Date = new Date();

    return zip(
      this.#jobRequestService.fetchOneJobWithId(jobId, JobSerialization.DETAILED),
      this.checklistRequestService.getAllChecklistsForJob(jobId),
      this.#jobRequestService.fetchJobsOfChildren(jobId),
      this.jobRequestService.registerActiveFillingOutUser(jobId)
    )
      .pipe(
        switchMap(([job, checklistInfos, jobsOfJob, registered]: [Job, ChecklistInfo[], Job[], boolean]) =>
          zip(
            (job.componentId || job.component?.id)
              ? this.objectRequestService.isObjectOutOfOrder(job.componentId || job.component.id)
              : of(false),
            job.chatId
              ? this.#chatService.getChatCounts(job.chatId)
              : of(null)
          )
            .pipe(
              map(([isObjectOutOfOrder, chatCounts]: [boolean, ChatCounts]) => ([
                job,
                checklistInfos,
                jobsOfJob,
                isObjectOutOfOrder,
                chatCounts,
                registered
              ]))
            )
        ),
        switchMap(([job, checklistInfos, jobsOfJob, isObjectOutOfOrder, chatCounts, ownUserRegisteredForFillOut]
                   : [Job, ChecklistInfo[], Job[], boolean, ChatCounts, boolean]
          ) => {
            const source: Observable<JobTemplateModel | null> = !!job?.jobRoutineId
              ? from(this.#jobRoutineTemplate.fetchJobRoutineById(job.jobRoutineId))
              : of(null);
            return source.pipe(
              map((jobRoutine: JobTemplateModel | null) => ([
                job,
                jobRoutine,
                checklistInfos,
                jobsOfJob,
                isObjectOutOfOrder,
                chatCounts,
                ownUserRegisteredForFillOut
              ]))
            );
          }
        ),
        delay(calcValue(+requestStartDate, JobFillOutXsState.REQUEST_DELAY)),
        map(
          ([job, jobRoutine, checklistInfos, jobsOfJob, isObjectOutOfOrder, chatCounts, ownUserRegisteredForFillOut]
           : [Job, JobTemplateModel, ChecklistInfo[], Job[], boolean, ChatCounts, boolean]) => {
            const visibleAccordions: Set<JobFillOutAccordion> = new Set();
            if (job.incidentCode !== 1 || job.overallAssessmentId != null || job.feedBack?.length || checklistInfos?.length > 0) {
              visibleAccordions.add(JobFillOutAccordion.JOB_ASSESSMENT);
            }
            if (checklistInfos.length > 0) {
              visibleAccordions.add(JobFillOutAccordion.CHECKLISTS);
            }
            if (jobsOfJob.length > 0) {
              visibleAccordions.add(JobFillOutAccordion.JOBS_OF_JOB);
            }
            if (job.messages.length > 0) {
              visibleAccordions.add(JobFillOutAccordion.MESSAGES_OF_JOB);
            }
            if (job.documents.length > 0) {
              visibleAccordions.add(JobFillOutAccordion.FILES);
            }
            visibleAccordions.add(JobFillOutAccordion.JOB_DETAILS);

            ctx.patchState({
              jobId,
              job,
              jobRoutine,
              isObjectOutOfOrder,
              checklistInfos,
              jobsOfJob,
              messagesOfJob: job.messages,
              files: job.documents.map(documentGroup => DocumentGroupModel.allDocumentsFromGroup(documentGroup)[0]),
              chatCounts,
              visibleAccordions,
              ownUserRegisteredForFillOut
            });

            this.setWorkflowOverview(ctx);
          }
        ),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  private setWorkflowOverview(ctx: StateContext<JobFillOutXsStateModel>): void {
    const state: JobFillOutXsStateModel = ctx.getState();

    const items: WorkflowOverviewItem[] = [];

    const objectEntry: WorkflowOverviewEntry = new WorkflowOverviewEntry();
    objectEntry.state = WorkflowOverviewItemState.FILLED_AND_CHECKED;
    objectEntry.label = 'DESIGN_B.JOB_FILL_OUT.CONTROL_BLOCKS.FILLED_DATA.OBJECT';
    items.push(objectEntry);

    if (state.visibleAccordions.has(JobFillOutAccordion.JOBS_OF_JOB)) {
      const jobsOfJobEntry: WorkflowOverviewEntry = new WorkflowOverviewEntry();
      jobsOfJobEntry.state = WorkflowOverviewItemState.FILLED;
      jobsOfJobEntry.label = 'DESIGN_B.JOB_FILL_OUT.CONTROL_BLOCKS.FILLED_DATA.JOBS_OF_JOB';
      jobsOfJobEntry.counterValue = state.jobsOfJob?.length.toString();
      items.push(jobsOfJobEntry);
    }

    if (state.visibleAccordions.has(JobFillOutAccordion.JOB_ASSESSMENT)) {
      const jobsOfJobEntry: WorkflowOverviewEntry = new WorkflowOverviewEntry();
      const signedChecklistsCount: number = state.checklistInfos.filter(
        checklist => checklist.mostRecentRevision?.status === ChecklistStatus.FILLED_AND_SIGNED
      ).length;
      if (state.job.overallAssessmentId || state.checklistInfos.length === signedChecklistsCount) {
        jobsOfJobEntry.state = WorkflowOverviewItemState.FILLED_AND_CHECKED;
      } else {
        jobsOfJobEntry.state = WorkflowOverviewItemState.NOT_FILLED;
      }
      jobsOfJobEntry.label = 'DESIGN_B.JOB_FILL_OUT.CONTROL_BLOCKS.FILLED_DATA.JOB_ASSESSMENT';
      items.push(jobsOfJobEntry);
    }

    if (state.visibleAccordions.has(JobFillOutAccordion.CHECKLISTS)) {
      const allChecklistsCount: number = state.checklistInfos.length;
      const filledChecklistsCount: number = state.checklistInfos.filter(
        checklist => checklist.mostRecentRevision?.status === ChecklistStatus.FILLED
          || checklist.mostRecentRevision?.status === ChecklistStatus.FILLED_AND_SIGNED
      ).length;
      const signedChecklistsCount: number = state.checklistInfos.filter(
        checklist => checklist.mostRecentRevision?.status === ChecklistStatus.FILLED_AND_SIGNED
      ).length;
      const checklistEntry: WorkflowOverviewEntry = new WorkflowOverviewEntry();
      if (filledChecklistsCount === 0) {
        checklistEntry.state = WorkflowOverviewItemState.NOT_FILLED;
      } else if (allChecklistsCount === signedChecklistsCount) {
        checklistEntry.state = WorkflowOverviewItemState.FILLED_AND_CHECKED;
      } else {
        checklistEntry.state = WorkflowOverviewItemState.FILLED;
      }
      checklistEntry.label = 'DESIGN_B.JOB_FILL_OUT.CONTROL_BLOCKS.FILLED_DATA.CHECKLISTS';
      checklistEntry.counterValue = `${signedChecklistsCount}/${allChecklistsCount}`;
      checklistEntry.counterLabel = 'DESIGN_B.JOB_FILL_OUT.CONTROL_BLOCKS.FILLED_DATA.CHECKLISTS_FILLED';
      items.push(checklistEntry);
    }

    const commentsAndHistoryEntry: WorkflowOverviewEntry = new WorkflowOverviewEntry();
    if (!state.chatCounts || state.chatCounts.userMessageCount == 0) {
      commentsAndHistoryEntry.state = WorkflowOverviewItemState.NOT_FILLED;
    } else if (state.chatCounts && state.chatCounts.newMessageCount > 0) {
      commentsAndHistoryEntry.state = WorkflowOverviewItemState.FILLED;
    } else {
      commentsAndHistoryEntry.state = WorkflowOverviewItemState.FILLED_AND_CHECKED;
    }
    commentsAndHistoryEntry.label = 'DESIGN_B.JOB_FILL_OUT.CONTROL_BLOCKS.FILLED_DATA.COMMENTS_AND_HISTORY';
    items.push(commentsAndHistoryEntry);

    if (state.visibleAccordions.has(JobFillOutAccordion.MESSAGES_OF_JOB)) {
      const messagesOfJobEntry: WorkflowOverviewEntry = new WorkflowOverviewEntry();
      messagesOfJobEntry.state = WorkflowOverviewItemState.FILLED_AND_CHECKED;
      messagesOfJobEntry.label = 'DESIGN_B.JOB_FILL_OUT.CONTROL_BLOCKS.FILLED_DATA.MESSAGES_OF_JOB';
      messagesOfJobEntry.counterValue = state.messagesOfJob?.length.toString();
      items.push(messagesOfJobEntry);
    }

    if (state.visibleAccordions.has(JobFillOutAccordion.FILES)) {
      const filesOfJobEntry: WorkflowOverviewEntry = new WorkflowOverviewEntry();
      if (state.files?.length > 0) {
        filesOfJobEntry.state = WorkflowOverviewItemState.FILLED_AND_CHECKED;
      } else {
        filesOfJobEntry.state = WorkflowOverviewItemState.NOT_FILLED;
      }
      filesOfJobEntry.label = 'DESIGN_B.JOB_FILL_OUT.CONTROL_BLOCKS.FILLED_DATA.FILES';
      filesOfJobEntry.counterValue = state.files?.length.toString();
      items.push(filesOfJobEntry);
    }

    const group: WorkflowOverviewGroup = new WorkflowOverviewGroup();
    group.items = items;

    ctx.patchState({
      workflowOverviewItems: [group]
    });
  }

  private showAccordions(ctx: StateContext<JobFillOutXsStateModel>, accordions: JobFillOutAccordion[], isChecklistEditMode = false): void {
    if (!accordions?.length) {
      return;
    }

    const visibleAccordions: Set<JobFillOutAccordion> = new Set(ctx.getState().visibleAccordions || new Set());

    for (const accordion of accordions) {
      if (!visibleAccordions.has(accordion)) {
        visibleAccordions.add(accordion);
      }
    }

    if (visibleAccordions.has(JobFillOutAccordion.CHECKLISTS)) {
      ctx.patchState({checklistEditMode: isChecklistEditMode});
    }

    ctx.patchState({visibleAccordions});
  }

  private hideAccordions(ctx: StateContext<JobFillOutXsStateModel>, accordions: JobFillOutAccordion[]): void {
    if (!accordions?.length) {
      return;
    }

    const visibleAccordions: Set<JobFillOutAccordion> = new Set(ctx.getState().visibleAccordions || new Set());

    for (const accordion of accordions) {
      if (visibleAccordions.has(accordion)) {
        visibleAccordions.delete(accordion);
        if (accordion == JobFillOutAccordion.CHECKLISTS) {
          ctx.patchState({checklistEditMode: false});
        }
      }
    }

    ctx.patchState({visibleAccordions});
  }

  //endregion

}
